import type { EnPracticeMap } from "./en-types";

/** English practice content: labour, tax, ip, notary-international, pdpa. */
export const EN_PRACTICE_B: EnPracticeMap = {
  labour: {
    slug: "labour",
    name: "Employment & Labour",
    tagline: "Employment contracts, work rules, termination and Labour Court disputes",
    seoTitle: "Employment Lawyers in Thailand — Contracts, Termination & Labour Court",
    seoDesc:
      "Employment contracts and work rules, lawful termination, severance, restructuring and Labour Court disputes, with document lists, real steps and common pitfalls.",
    answer:
      "Employment work covers drafting employment contracts and work rules, advising on lawful termination and severance, handling restructuring and transfers, and representing either side in Labour Court proceedings. Most disputes we see come from documents that were never signed or never updated, so the firm reviews the paperwork before advising on any termination.",
    audience: [
      "Employers writing their first contracts and work rules",
      "Employers planning a termination or a restructuring",
      "Employees who were dismissed and want to know their entitlements",
      "Companies facing a claim before the Labour Court",
      "HR teams reviewing probation, bonus and non-compete wording",
    ],
    scope: [
      "Drafting employment contracts, probation terms and job descriptions",
      "Preparing and updating work rules and employee handbooks",
      "Advice on warnings, disciplinary process and lawful termination",
      "Calculating severance and other statutory payments",
      "Restructuring, business transfers and mutual-separation packages",
      "Representation in Labour Court claims and settlement negotiation",
    ],
    examples: [
      "An employer wants a bilingual contract that matches actual practice",
      "A poor-performance dismissal must be documented properly first",
      "An employee claims unfair dismissal and unpaid severance",
      "A company closing a department needs a lawful process",
      "A non-compete and confidentiality clause needs enforceable wording",
      "A foreign employee's contract must fit their work-permit conditions",
    ],
    docs: [
      "Employment contract and any amendments",
      "Work rules, handbook and acknowledgement records",
      "Payslips and payroll records for the relevant period",
      "Warning letters, performance records and meeting minutes",
      "Termination letter or resignation letter, if any",
      "Company affidavit and the authorised signatory's details",
    ],
    steps: [
      "Send the contract, work rules and payroll records for review",
      "We identify what the documents support and where the gaps are",
      "We advise on the lawful process and prepare the necessary letters",
      "We handle negotiation or mediation where a settlement is realistic",
      "We file or defend the Labour Court claim if no settlement is reached",
      "We report after each hearing and advise on enforcement or compliance",
    ],
    cautions: [
      {
        title: "Undocumented performance problems are hard to rely on",
        body: "Where a dismissal is based on performance or conduct, the records made at the time carry the weight. Warnings written after the decision rarely help and often make the position worse.",
      },
      {
        title: "Work rules must reflect actual practice",
        body: "Rules copied from a template and never applied create inconsistency between the document and reality. That inconsistency is what disputes are built on, so update the rules when practice changes.",
      },
      {
        title: "Severance is not the only payment to consider",
        body: "Depending on the circumstances there may be notice, unused annual leave and other statutory items. Calculating only one of them is a frequent cause of a claim after an otherwise agreed separation.",
      },
    ],
    compare: {
      heading: "Resignation, mutual separation and termination by the employer",
      intro: "The three routes differ in documentation, payment and the risk of a later claim.",
      columns: ["Resignation", "Mutual separation", "Employer termination"],
      rows: [
        { label: "Who initiates", cells: ["The employee", "Both parties", "The employer"] },
        {
          label: "Core document",
          cells: ["Resignation letter with an effective date", "Signed separation agreement", "Termination letter stating the ground and date"],
        },
        {
          label: "Payments",
          cells: ["Outstanding wages and accrued entitlements", "As agreed, recorded in writing", "Statutory entitlements depending on the ground and length of service"],
        },
        { label: "Risk of a later claim", cells: ["Lowest", "Low where the release is properly worded", "Highest where the ground or process is not documented"] },
        {
          label: "Watch out for",
          cells: ["Pressured resignations treated as dismissal", "Vague release wording", "A ground that the records do not support"],
        },
      ],
      note: "Each situation depends on its own documents and facts. Have the paperwork reviewed before any decision is communicated.",
    },
    handoff:
      "The firm can take the whole employment file: contracts and work rules, the process for a specific termination, negotiation, and representation before the Labour Court, together with certified translations where foreign employees are involved.",
    faqs: [
      {
        question: "Must an employment contract be in writing?",
        answer:
          "An employment relationship can exist without a written contract, but written terms are what protect both sides in a dispute. Bilingual contracts are common for foreign employees; state clearly which language prevails if the two versions differ.",
      },
      {
        question: "Can an employee be dismissed during probation?",
        answer:
          "Probation does not remove statutory protections, and the entitlements depend on length of service and the ground for dismissal. Recording performance during probation, rather than only at its end, is what makes a probation decision defensible.",
      },
      {
        question: "How is severance calculated?",
        answer:
          "It is based on length of service and the wage rate, with statutory bands, and there are grounds on which it is not payable. Because other items such as notice and unused leave may also arise, the calculation is done on the full record rather than the contract alone.",
      },
      {
        question: "What makes a dismissal unfair?",
        answer:
          "In broad terms, a dismissal without adequate reason or without a proper process. The employer's contemporaneous records — warnings, meeting notes, performance reviews — are usually decisive, which is why they are reviewed before any decision is made.",
      },
      {
        question: "Are non-compete clauses enforceable in Thailand?",
        answer:
          "They can be, if the restriction is reasonable in scope, duration and geography and protects a legitimate interest. Blanket restrictions covering an entire industry indefinitely are much more likely to be cut down or disregarded.",
      },
      {
        question: "How long does an employee have to bring a claim?",
        answer:
          "Different claims have different time limits, and the starting point depends on the type of claim. Because a late claim can be barred, both employers and employees should get the dates checked early rather than after prolonged negotiation.",
      },
      {
        question: "What happens when a business is transferred?",
        answer:
          "Employee positions and accrued entitlements need to be addressed in the transaction documents, and employee consent may be required depending on the structure. Handling this as an afterthought is a common source of claims after a deal closes.",
      },
      {
        question: "Does a Labour Court case require lawyers on both sides?",
        answer:
          "Parties may appear themselves, and the court encourages conciliation. Representation helps where the evidence is document-heavy, where a foreign party needs interpretation, or where the calculation of entitlements is disputed.",
      },
    ],
    related: ["corporate", "litigation", "pdpa"],
    imageAlt: "An employment lawyer reviewing contracts and work rules with an HR manager in an office",
  },

  tax: {
    slug: "tax",
    name: "Tax",
    tagline: "Tax planning, filings, audits and appeals",
    seoTitle: "Tax Lawyers in Thailand — Compliance, Audits & Appeals",
    seoDesc:
      "Corporate and personal tax positions, VAT and withholding, transaction tax planning, Revenue Department audits and appeals, with documents, steps and pitfalls.",
    answer:
      "Tax work covers reviewing a company's or an individual's tax position, structuring transactions before they are signed, dealing with Revenue Department enquiries and audits, and filing objections or appeals against assessments. The firm works from the underlying contracts and accounting records, because the tax treatment follows the documents rather than the intention.",
    audience: [
      "Companies that received a Revenue Department enquiry or assessment",
      "Businesses reviewing VAT and withholding treatment before a filing",
      "Parties structuring a share, asset or property transaction",
      "Foreign individuals or companies with Thai-source income",
      "Groups reviewing cross-border payments and treaty positions",
    ],
    scope: [
      "Review of corporate and personal tax positions",
      "VAT, withholding tax and stamp-duty treatment of specific transactions",
      "Tax structuring for share, asset and property transactions",
      "Responding to Revenue Department enquiries and audits",
      "Objections and appeals against assessments",
      "Cross-border payments and double-tax-treaty analysis",
    ],
    examples: [
      "An assessment arrives and the deadline to object is running",
      "Withholding on service payments to an overseas supplier is unclear",
      "A share sale needs its tax consequences confirmed before signature",
      "A property transfer needs the fees and taxes calculated in advance",
      "Intercompany charges need supporting documentation",
      "An individual moving to Thailand needs their residence position reviewed",
    ],
    docs: [
      "Company affidavit and financial statements for the relevant years",
      "Tax returns and filing receipts",
      "The contracts and invoices underlying the transactions in question",
      "Bank statements evidencing the payments",
      "Any assessment, enquiry letter or audit correspondence received",
      "Withholding certificates and VAT records",
    ],
    steps: [
      "Send the assessment or the transaction documents and the filings",
      "We analyse the treatment against the contracts and the records",
      "We set out the position, the exposure and the available options",
      "We prepare the response, objection or appeal within the deadline",
      "We deal with the officer or the appeal body on your behalf",
      "We advise on documentation changes so the same issue does not recur",
    ],
    cautions: [
      {
        title: "Objection deadlines are short and strict",
        body: "An assessment carries a limited window to object. Losing that window narrows the options considerably, so the letter should reach a lawyer as soon as it arrives rather than after internal discussion.",
      },
      {
        title: "The contract wording drives the tax treatment",
        body: "How a payment is described in the contract and the invoice affects withholding, VAT and deductibility. Fixing the wording before signature is far cheaper than arguing about substance during an audit.",
      },
      {
        title: "Supporting documents matter as much as the return",
        body: "Deductions and treaty benefits typically depend on documentation held at the time. Reconstructing it after an enquiry begins is rarely as persuasive as records kept contemporaneously.",
      },
    ],
    compare: {
      heading: "Handling an assessment: pay, object, or appeal",
      intro: "These options are sequential rather than alternative, and each has its own deadline and document set.",
      columns: ["Pay the assessment", "File an objection", "Appeal further"],
      rows: [
        { label: "When it applies", cells: ["The assessment is correct", "Within the objection window", "After an objection decision you dispute"] },
        {
          label: "Documents required",
          cells: ["Assessment and payment record", "Objection, grounds and supporting evidence", "Case file, the decision, and full documentary support"],
        },
        { label: "Time involved", cells: ["Shortest", "Moderate", "Longest"] },
        { label: "Effect on exposure", cells: ["Closes the matter for that item", "May reduce or cancel the assessment", "Reviewed by a further body or the court"] },
        {
          label: "Watch out for",
          cells: ["Paying an item that was wrongly assessed", "Missing the deadline or filing bare grounds", "Weak documentation carried forward from the earlier stage"],
        },
      ],
      note: "The correct route depends on the assessment and the records behind it. Have both reviewed before responding.",
    },
    handoff:
      "The firm can handle the tax file alongside the corporate and contractual work, so the documents, the filings and the response to the Revenue Department are consistent rather than prepared by separate advisers.",
    faqs: [
      {
        question: "I received a Revenue Department letter. What should I do first?",
        answer:
          "Note the response deadline and identify exactly which years and items are in question. Then gather the returns, financial statements and the contracts behind those items before replying, because an early answer that is inconsistent with the documents is difficult to correct later.",
      },
      {
        question: "How does withholding tax work on payments to overseas suppliers?",
        answer:
          "It depends on the nature of the payment and on whether a double-tax treaty applies, and the documentation supporting treaty relief usually needs to exist at the time of payment. Getting the contractual description right beforehand avoids most disputes.",
      },
      {
        question: "Is a share sale taxed differently from an asset sale?",
        answer:
          "Yes, the consequences differ for both seller and buyer, and stamp duty, VAT and the treatment of goodwill can all vary. Because the structure is difficult to change after signature, this is analysed before the transaction documents are finalised.",
      },
      {
        question: "When must a business register for VAT?",
        answer:
          "Registration is required once the statutory threshold or activity conditions are met, and some businesses register voluntarily. Late registration and incorrect invoicing are common issues that surface during an audit rather than at the time.",
      },
      {
        question: "How is tax residence determined for individuals?",
        answer:
          "Presence in Thailand during a tax year is the primary factor, and residence affects which income is taxable and how foreign income is treated. Anyone relocating should have the position reviewed before the first filing rather than after.",
      },
      {
        question: "Can penalties and surcharges be reduced?",
        answer:
          "There are circumstances in which they may be mitigated, and the outcome depends on the facts, the documentation and how the matter is presented. No adviser can promise a particular reduction, and we do not.",
      },
      {
        question: "What documentation supports intercompany charges?",
        answer:
          "A written agreement, evidence that the service was actually provided, a basis for the charge, and invoices consistent with all of it. Charges with no supporting record are the items most often disallowed on review.",
      },
      {
        question: "Do you handle the accounting as well?",
        answer:
          "We work on the legal and tax position and coordinate with your accountants, or with an accounting provider where you do not have one, so the filings and the underlying contracts are consistent.",
      },
    ],
    related: ["corporate", "property", "litigation"],
    imageAlt: "A tax lawyer reviewing financial statements and assessment correspondence at a desk",
  },

  ip: {
    slug: "ip",
    name: "Intellectual Property",
    tagline: "Trademarks, copyright, trade secrets and enforcement",
    seoTitle: "IP Lawyers in Thailand — Trademarks, Copyright & Enforcement",
    seoDesc:
      "Trademark searches and filings, copyright and trade-secret protection, licensing, and action against infringement in Thailand, with documents, steps and pitfalls.",
    answer:
      "IP work covers trademark availability searches and filings, responding to registry objections and oppositions, protecting copyright and trade secrets by contract, licensing and assignment, and taking action against infringement. The firm searches before filing, because a rejected application is slower and costlier to fix than a name changed at the outset.",
    audience: [
      "Businesses registering a brand name or logo in Thailand",
      "Owners whose application received a registry objection",
      "Creators and studios who need ownership of their work documented",
      "Companies licensing a brand or technology in or out of Thailand",
      "Owners facing counterfeits or online infringement",
    ],
    scope: [
      "Trademark availability searches and class selection",
      "Filing, prosecution and responses to objections and oppositions",
      "Renewals, assignments and recordal of changes",
      "Copyright ownership, works-for-hire and assignment documentation",
      "Trade-secret and confidentiality frameworks",
      "Enforcement: warning letters, takedowns and court proceedings",
    ],
    examples: [
      "A new brand needs clearance before launch and packaging print",
      "A registry objection cites a similar earlier mark",
      "A freelancer's work must be assigned to the company that paid for it",
      "A distributor is using the brand beyond the licence terms",
      "Counterfeit goods appear on an online marketplace",
      "A franchise package needs its IP terms drafted",
    ],
    docs: [
      "The mark: wordmark, logo file and any variants used",
      "A description of the goods or services actually offered",
      "Applicant details: company affidavit or passport or ID card",
      "Evidence of earliest use: packaging, invoices, advertising",
      "Existing registrations abroad and any priority claim documents",
      "Contracts with creators, agencies, employees and distributors",
    ],
    steps: [
      "Send the mark and the list of goods or services you actually offer",
      "We search availability and advise on classes and risk",
      "We file the application and monitor the registry",
      "We respond to any objection or opposition within the deadline",
      "We record renewals, assignments and changes as needed",
      "We act on infringement with warning letters, takedowns or proceedings",
    ],
    cautions: [
      {
        title: "Rights are territorial",
        body: "A registration obtained abroad does not by itself protect the mark in Thailand. Filing locally, in the classes matching your actual activity, is what supports enforcement here.",
      },
      {
        title: "Descriptive marks are hard to register and hard to enforce",
        body: "Names that merely describe the goods or services often face objections, and even when registered they are weak against similar names. A distinctive element makes both registration and enforcement realistic.",
      },
      {
        title: "Paying for work is not the same as owning it",
        body: "Without an assignment or the right contractual wording, copyright in commissioned work may not sit with the business that paid for it. Fixing this after a dispute or an investment round starts is much harder.",
      },
    ],
    compare: {
      heading: "Trademark, copyright and trade secret",
      intro: "These protect different things and are obtained in different ways. Most businesses need more than one.",
      columns: ["Trademark", "Copyright", "Trade secret"],
      rows: [
        { label: "What it protects", cells: ["Names, logos and marks identifying goods or services", "Original works such as text, images, code and film", "Confidential business information with commercial value"] },
        { label: "How it arises", cells: ["Registration gives the strongest position", "Arises on creation; records help prove it", "Kept by confidentiality measures and contracts"] },
        { label: "Duration", cells: ["Renewable in terms", "A long statutory term depending on the work", "For as long as secrecy is maintained"] },
        { label: "Key document", cells: ["Registration certificate", "Assignment or work-for-hire wording", "NDA plus internal access controls"] },
        { label: "Common pitfall", cells: ["Filing in the wrong classes", "No assignment from freelancers", "No access control, so secrecy cannot be shown"] },
      ],
      note: "The right combination depends on what your business actually sells and how it is produced.",
    },
    handoff:
      "The firm can manage the IP portfolio alongside the commercial contracts: searches and filings, the licence or assignment wording, and enforcement where infringement occurs, including certified translations of foreign registrations.",
    faqs: [
      {
        question: "Why search before filing a trademark?",
        answer:
          "A search shows earlier similar marks in the relevant classes, so you can adjust the mark or the classes before spending on filing, packaging and marketing. It cannot guarantee registration, but it identifies the obstacles that cause most refusals.",
      },
      {
        question: "How are classes chosen?",
        answer:
          "Classes follow the goods and services you actually offer, and near-term plans can be included. Filing in too few leaves gaps a competitor can occupy; filing in classes you will never use adds cost and can attract objections.",
      },
      {
        question: "Does my foreign registration protect me in Thailand?",
        answer:
          "Not by itself, because trademark rights are territorial. There are international filing routes that designate Thailand, and there are national filings; which is preferable depends on your existing portfolio and timing.",
      },
      {
        question: "Does copyright need to be registered?",
        answer:
          "Copyright arises on creation without registration, but recording the work and its ownership makes proof much easier in a dispute. The more important document is usually the assignment or employment wording that puts ownership where it belongs.",
      },
      {
        question: "What should a licence agreement cover?",
        answer:
          "The scope of rights, territory and term, quality control and approval rights, royalties and reporting, sub-licensing, what happens on termination, and who may enforce against third parties. Ambiguity in scope is the most frequent source of licensing disputes.",
      },
      {
        question: "What can be done about counterfeits online?",
        answer:
          "Options include platform takedown requests supported by your registration, warning letters, and court proceedings. Preparing the evidence — listings, purchases, comparison of goods — before acting makes each of those routes considerably stronger.",
      },
      {
        question: "How long does trademark registration take in Thailand?",
        answer:
          "It varies with the registry's workload and with whether an objection or opposition arises. Since the timeline is outside anyone's control, we plan launches so that use of the brand is not entirely dependent on the certificate arriving by a fixed date.",
      },
      {
        question: "How do I protect a trade secret in practice?",
        answer:
          "Identify the information, restrict access to those who need it, use confidentiality agreements with employees and partners, and keep records of the measures taken. Without demonstrable measures, it becomes difficult to argue the information was secret at all.",
      },
    ],
    related: ["corporate", "litigation", "pdpa"],
    imageAlt: "A lawyer comparing brand artwork and trademark registration papers on a meeting table",
  },

  "notary-international": {
    slug: "notary-international",
    name: "Notarial Services & International Documents",
    tagline: "Notarial Services Attorney, certified translation and legalisation for use abroad",
    seoTitle: "Notarial Services in Thailand — Certification, Translation & Legalisation",
    seoDesc:
      "Notarial Services Attorney certification, certified translation and legalisation of Thai documents for use overseas, with document lists, correct order of steps and pitfalls.",
    answer:
      "This practice covers certification by a Notarial Services Attorney, certified translation, and legalisation of documents so they can be used abroad — or so foreign documents can be used in Thailand. The order of the steps is what usually goes wrong: the receiving authority's requirement is confirmed first, then translation, certification and legalisation follow in the sequence that authority accepts.",
    audience: [
      "Students and applicants sending Thai documents to a foreign institution",
      "People signing a power of attorney for use in another country",
      "Companies certifying corporate documents for an overseas transaction",
      "Foreigners whose documents must be usable before a Thai authority",
      "Anyone whose document was rejected abroad and must be redone",
    ],
    scope: [
      "Certification of signatures, copies and declarations by a Notarial Services Attorney",
      "Certified translation of Thai and foreign documents",
      "Legalisation through the channel the receiving authority requires",
      "Powers of attorney and affidavits for use abroad",
      "Corporate document sets for overseas filings and transactions",
      "Advice on the correct sequence for a specific destination country",
    ],
    examples: [
      "A university abroad requires certified copies of a transcript and degree",
      "A power of attorney must be signed in Thailand and used overseas",
      "Company documents are needed for an overseas bank or registry",
      "An affidavit is required for an immigration or court process abroad",
      "A foreign marriage or birth certificate must be used in Thailand",
      "A document was rejected because the steps were done in the wrong order",
    ],
    docs: [
      "The original document, plus the copies to be certified",
      "Passport or ID card of the person signing",
      "The receiving authority's written requirement, if you have it",
      "The destination country and the purpose of use",
      "Company affidavit and authority documents, for corporate documents",
      "Any previous certification or translation already obtained",
    ],
    steps: [
      "Tell us the destination country, the receiving authority and the purpose",
      "We confirm the form of certification that authority accepts",
      "We prepare the certified translation where one is needed",
      "The Notarial Services Attorney certifies the signature, copy or declaration",
      "We arrange legalisation through the required channel",
      "We hand over the completed set with copies for your records",
    ],
    cautions: [
      {
        title: "Confirm the requirement before starting",
        body: "Different countries and institutions accept different forms and sequences. Starting without the receiving authority's requirement in writing is the main reason documents come back and have to be redone.",
      },
      {
        title: "Sequence errors cannot be patched",
        body: "Translating after certification, or legalising before the certification the authority expects, usually means repeating steps from the beginning. The order is set at the outset for that reason.",
      },
      {
        title: "Certification confirms form, not the underlying facts",
        body: "A Notarial Services Attorney certifies matters such as a signature or that a copy corresponds to the original. It is not a verification of the truth of the document's contents, and no one should represent it that way.",
      },
    ],
    compare: {
      heading: "Notarial certification, certified translation and legalisation",
      intro: "Most files need more than one of these. This shows what each step actually does.",
      columns: ["Notarial certification", "Certified translation", "Legalisation"],
      rows: [
        { label: "What it does", cells: ["Certifies a signature, a copy or a declaration", "Produces a translation with a certification of accuracy", "Confirms the document for official use in another jurisdiction"] },
        { label: "Who performs it", cells: ["A Notarial Services Attorney", "A qualified translator, certified as required", "The authority or channel the destination accepts"] },
        { label: "Needed when", cells: ["The receiving party requires certified signatures or copies", "The document is not in the required language", "The destination requires official confirmation"] },
        { label: "Typical inputs", cells: ["Original document and the signatory's identification", "The source document and the target language", "The certified and translated document"] },
        { label: "Common pitfall", cells: ["Certifying a document the destination does not accept in that form", "Translating before the final version is confirmed", "Doing it before the certification step it depends on"] },
      ],
      note: "We confirm the requirement of the specific receiving authority before beginning, so the sequence is right the first time.",
    },
    handoff:
      "Because translation, notarial certification and legalisation are handled in the same office, the file moves through the steps in the correct order without being passed between separate providers. Fees are quoted per document set after we see the requirement.",
    faqs: [
      {
        question: "What does a Notarial Services Attorney in Thailand do?",
        answer:
          "A lawyer licensed as a Notarial Services Attorney certifies matters such as a signature, that a copy corresponds to an original, or a declaration made before them. It confirms form and execution rather than verifying that the document's contents are true.",
      },
      {
        question: "Which comes first, translation or certification?",
        answer:
          "It depends on the receiving authority. Some require the original certified first and then translated; others require a certified translation before legalisation. Because reversing the order usually means redoing the work, we confirm the requirement before starting.",
      },
      {
        question: "Does Thailand issue an apostille?",
        answer:
          "Thailand's legalisation of documents for overseas use runs through its own channel rather than an apostille under the Hague Convention on Legalisation. Which form your destination accepts should be confirmed with that receiving authority before the file is prepared.",
      },
      {
        question: "Can a power of attorney be signed here and used abroad?",
        answer:
          "Yes, and it commonly requires certification of the signature, a certified translation into the destination language, and legalisation. The wording also has to satisfy the receiving institution, so send their template or requirement if you have one.",
      },
      {
        question: "How can a foreign document be used in Thailand?",
        answer:
          "Usually it is certified in its country of origin, then translated into Thai with the translation certified, and legalised through the route the Thai receiving authority requires. Different Thai authorities can require slightly different forms.",
      },
      {
        question: "How long does the whole process take?",
        answer:
          "It depends on the document type, the destination and the channels involved, and parts of it are outside the firm's control. We tell you which steps have external timelines so you can plan submission deadlines with a margin.",
      },
      {
        question: "Do I have to attend in person?",
        answer:
          "Certification of your own signature requires you to sign before the Notarial Services Attorney with identification. Certification of copies and translation work can often proceed without you present once the originals are available.",
      },
      {
        question: "My document was rejected abroad. Can it be fixed?",
        answer:
          "Often yes, but it usually means repeating the steps in the correct sequence. Send the rejection notice, because it normally states exactly which form the receiving authority requires, which is what determines the corrected route.",
      },
    ],
    related: ["family", "corporate", "estate"],
    imageAlt: "A Notarial Services Attorney certifying a document with a seal beside its certified translation",
  },

  pdpa: {
    slug: "pdpa",
    name: "Data Protection & PDPA",
    tagline: "Privacy policies, consent, data-subject requests and incident response",
    seoTitle: "PDPA Lawyers in Thailand — Privacy Compliance & Incidents",
    seoDesc:
      "Thai PDPA compliance: privacy notices and consent, records of processing, data-processing agreements, data-subject requests and incident response, with steps and pitfalls.",
    answer:
      "This practice covers bringing an organisation's personal-data handling in line with Thailand's PDPA: mapping what data is collected and why, drafting privacy notices and consent language, putting data-processing agreements in place with vendors, handling data-subject requests, and responding to incidents. The firm starts from what the business actually does with data, not from a template.",
    audience: [
      "Businesses collecting customer data online or in store",
      "Companies with HR and recruitment data to handle properly",
      "Organisations using overseas cloud or marketing vendors",
      "Teams that received a data-subject access or deletion request",
      "Organisations dealing with a suspected data breach",
    ],
    scope: [
      "Data mapping and lawful-basis analysis for each processing activity",
      "Privacy notices, consent forms and cookie notices",
      "Records of processing and internal policies",
      "Data-processing agreements with vendors and cross-border transfer terms",
      "Procedures for data-subject requests",
      "Incident response, assessment and notification obligations",
    ],
    examples: [
      "A website needs a privacy notice and cookie handling that match reality",
      "Recruitment data is retained indefinitely with no policy",
      "A marketing vendor abroad processes customer lists without an agreement",
      "A customer asks for deletion of their data",
      "CCTV and access-control data are collected without notice",
      "A suspected breach requires assessment against notification duties",
    ],
    docs: [
      "A list of the systems and vendors that hold personal data",
      "Existing privacy notice, consent forms and internal policies",
      "Vendor contracts, especially cloud and marketing providers",
      "HR forms and recruitment materials",
      "Records of any data-subject requests already received",
      "Incident logs or vendor reports, where an incident is involved",
    ],
    steps: [
      "Walk us through what data the business collects and where it goes",
      "We map the processing activities and identify the lawful basis for each",
      "We draft the notices, consent wording and internal policies",
      "We put the vendor and transfer terms in place",
      "We set up the process for data-subject requests and incidents",
      "We review again when systems, vendors or activities change",
    ],
    cautions: [
      {
        title: "Consent is not the answer to everything",
        body: "Relying on consent where another lawful basis fits better creates a fragile position, because consent can be withdrawn. Each activity should be matched to the basis that actually supports it.",
      },
      {
        title: "A published policy that nobody follows is worse than none",
        body: "If the notice promises retention limits or controls the organisation does not implement, the document itself becomes evidence of the gap. Policies and practice are aligned rather than drafted separately.",
      },
      {
        title: "Vendor arrangements are part of your compliance",
        body: "Using a processor without appropriate terms, or transferring data overseas without addressing the transfer conditions, leaves the organisation exposed even where the vendor is at fault.",
      },
    ],
    compare: {
      heading: "Consent, contractual necessity and legitimate interest",
      intro: "Choosing the right lawful basis determines what you must document and what a data subject can require of you.",
      columns: ["Consent", "Contractual necessity", "Legitimate interest"],
      rows: [
        { label: "Best suited to", cells: ["Marketing and optional activities", "Processing needed to deliver the service", "Security, fraud prevention and comparable purposes"] },
        { label: "What to document", cells: ["Clear, specific consent records and withdrawal", "The contract and why the data is necessary", "A balancing assessment and mitigations"] },
        { label: "Data subject's position", cells: ["May withdraw consent at any time", "Cannot withdraw while the contract requires it", "May object; the balance is reconsidered"] },
        { label: "Main risk", cells: ["Bundled or unclear consent is ineffective", "Collecting more than the contract needs", "No assessment on file to show the balance"] },
        { label: "Common pitfall", cells: ["Using consent for everything", "Treating optional extras as necessary", "Asserting it without any documented analysis"] },
      ],
      note: "The right basis depends on the specific activity. A single organisation normally relies on several across different activities.",
    },
    handoff:
      "The firm can take the whole programme: the data mapping, the notices and policies, the vendor terms, the request and incident procedures, and training for the team that handles requests day to day.",
    faqs: [
      {
        question: "Which organisations must comply with the PDPA?",
        answer:
          "In broad terms, organisations that collect, use or disclose personal data in Thailand, and in defined circumstances those outside Thailand that handle data of people here. Size is not the deciding factor, so small businesses handling customer or employee data are within scope.",
      },
      {
        question: "Is a privacy notice on the website enough?",
        answer:
          "No. The notice is the visible part, but compliance rests on knowing what data you hold, having a lawful basis for each use, controlling vendor access, and being able to handle requests and incidents. A notice that does not match practice creates risk of its own.",
      },
      {
        question: "How should a data-subject request be handled?",
        answer:
          "Verify who is asking, identify what data you hold and where, decide what the request covers under the applicable exceptions, and respond within the required timeframe with a record of what you did. Having the procedure written before the first request is what makes this manageable.",
      },
      {
        question: "What should happen after a suspected breach?",
        answer:
          "Contain it, then assess what data and how many people are affected and what the risk is, because that assessment drives whether notification is required and in what timeframe. Keep the log from the first hour; it is what the assessment is later built on.",
      },
      {
        question: "Can personal data be sent to overseas vendors?",
        answer:
          "Cross-border transfers are permitted where the conditions in the law are met, which usually means addressing them in the vendor contract and documenting the basis. Using an overseas cloud provider without those terms is one of the most common gaps we see.",
      },
      {
        question: "Do employees' and applicants' data need the same treatment?",
        answer:
          "Yes. HR and recruitment data is personal data, and retention of unsuccessful applicants' files without a policy is a frequent issue. Sensitive categories such as health information require additional care.",
      },
      {
        question: "Is CCTV covered?",
        answer:
          "Images of identifiable people are personal data, so notice, purpose limitation, retention and access controls all apply. Cameras installed without any notice or retention rule are a common finding in reviews.",
      },
      {
        question: "Does the organisation need a data protection officer?",
        answer:
          "That depends on the nature and scale of the processing and on the criteria in the law. Where one is not required, someone still needs clear responsibility for requests and incidents, otherwise deadlines are missed by default.",
      },
    ],
    related: ["corporate", "labour", "ip"],
    imageAlt: "A lawyer reviewing a data-processing map and privacy documentation on a laptop and printed sheets",
  },
};
